<template>
  <div class="who-step min-h-full">
    <div class="flex items-center gap-2 mb-4">
      <h2 class="text-lg font-medium text-gray-900">WHO - Agents impliqués</h2>
      <BookCheckIcon v-show="isStepValid" class="w-5 h-5 text-green-500" />
    </div>



    <div class="space-y-6" :class="{ 'opacity-90': readOnlyConfig?.isReadOnly }">
      <!-- Sélecteur d'agent modernisé - MASQUÉ en mode readonly -->
      <div v-if="!readOnlyConfig?.isReadOnly" class="relative">
        <div class="relative flex items-center">
          <input id="agent-search" v-model="searchQuery" type="text" name="agent-search"
            placeholder="Rechercher un agent..."
            class="w-full p-2 border border-gray-300 rounded-md mb-0 no-margin-bottom" :disabled="isLoading"
            @input="handleSearch" @focus="handleFocus" @blur="handleBlur" />
          <button v-if="searchQuery || showResults" title="Réinitialiser"
            class="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-red-500 hover:text-red-700"
            @click="resetSearch" @mousedown.prevent>
            <XIcon class="h-4 w-4" />
          </button>
        </div>

        <!-- Dropdown avec sections séparées -->
        <div v-if="showResults"
          class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-96 overflow-y-auto">
          <!-- Bouton créer -->
          <div title="Créer un nouvel agent"
            class="p-3 hover:bg-gray-100 cursor-pointer text-blue-600 border-b border-gray-100 flex items-center gap-2"
            @click="openCreator">
            <PlusIcon class="h-4 w-4" />
            <span>Créer un nouvel agent</span>
          </div>

          <!-- Section : Mes agents -->
          <div v-if="myAgents.length > 0" class="section">
            <div class="section-header p-2 bg-blue-50 border-b border-blue-100 flex items-center gap-2">
              <UserIcon class="h-4 w-4 text-blue-600" />
              <span class="text-sm font-medium text-blue-900">Mes agents ({{ myAgents.length }})</span>
            </div>
            <div v-for="agent in myAgents" :key="agent.uuid"
              class="p-3 hover:bg-gray-100 cursor-pointer flex justify-between items-center border-l-3 border-blue-500"
              @click="selectAgent(agent)">
              <div class="flex-1">
                <div class="font-medium text-gray-900 flex items-center gap-2">
                  <BuildingIcon v-if="agent.is_legal_entity" class="h-4 w-4 text-gray-600" />
                  <UserIcon v-else class="h-4 w-4 text-gray-600" />
                  {{ getDisplayName(agent) }}
                </div>
                <div v-if="agent.affiliation && !agent.is_legal_entity" class="text-sm text-gray-600">
                  {{ agent.affiliation }}
                </div>
                <div v-if="agent.group" class="text-xs text-gray-500 mt-1">
                  Groupe: {{ agent.group }}
                </div>
                <div v-if="agent.roles" class="text-xs text-blue-600 mt-1">
                  {{ agent.roles }}
                </div>
              </div>
            </div>
          </div>

          <!-- Section : Agents publics -->
          <div v-if="publicAgents.length > 0" class="section">
            <div class="section-header p-2 bg-emerald-50 border-b border-emerald-100 flex items-center gap-2">
              <GlobeIcon class="h-4 w-4 text-emerald-600" />
              <span class="text-sm font-medium text-emerald-900">Agents publics ({{ publicAgents.length }})</span>
            </div>
            <div v-for="agent in publicAgents" :key="agent.uuid"
              class="p-3 hover:bg-gray-100 cursor-pointer flex justify-between items-center border-l-3 border-emerald-500"
              @click="selectAgent(agent)">
              <div class="flex-1">
                <div class="font-medium text-gray-900 flex items-center gap-2">
                  <BuildingIcon v-if="agent.is_legal_entity" class="h-4 w-4 text-gray-600" />
                  <UserIcon v-else class="h-4 w-4 text-gray-600" />
                  {{ getDisplayName(agent) }}
                  <span
                    class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                    <GlobeIcon class="h-3 w-3 mr-1" />
                    Public
                  </span>
                </div>
                <div v-if="agent.affiliation && !agent.is_legal_entity" class="text-sm text-gray-600">
                  {{ agent.affiliation }}
                </div>
                <div v-if="agent.group" class="text-xs text-gray-500 mt-1">
                  Groupe: {{ agent.group }}
                </div>
                <div v-if="agent.roles" class="text-xs text-emerald-600 mt-1">
                  {{ agent.roles }}
                </div>
              </div>
            </div>
          </div>

          <!-- Message si aucun agent -->
          <div v-if="myAgents.length === 0 && publicAgents.length === 0" class="p-4 text-center text-gray-500">
            <div class="flex flex-col items-center gap-2">
              <UsersIcon class="h-8 w-8 text-gray-400" />
              <span>Aucun agent trouvé</span>
              <span class="text-xs">Créez-en un nouveau pour commencer</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Créateur/Éditeur d'agent - MASQUÉ en mode readonly -->
      <AgentCreator v-if="showCreator && !readOnlyConfig?.isReadOnly" :user-id="userId" :show="showCreator"
        :mode="creatorMode" :initial-data="selectedAgentForEdit" @update:show="showCreator = $event"
        @agent-created="handleAgentCreated" @agent-updated="handleAgentUpdated" />

      <!-- Liste des agents sélectionnés -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div v-for="agent in selectedAgents" :key="agent.uuid" class="anamnesis-card relative group"
          :class="{ 'border-emerald-200 bg-emerald-50': isPublicAgent(agent) }">
          <!-- Actions en haut à droite - MASQUÉES en mode readonly -->
          <div v-if="!readOnlyConfig?.isReadOnly" class="absolute top-2 right-2 flex items-center gap-1">
            <!-- Bouton éditer (seulement pour mes agents) -->
            <button v-if="canEditAgent(agent)" title="Modifier l'agent"
              class="text-blue-500 hover:text-blue-700 opacity-0 group-hover:opacity-100 transition-opacity"
              @click="editAgent(agent)">
              <EditIcon class="h-4 w-4" />
            </button>
            <!-- Bouton supprimer -->
            <button title="Retirer de la sélection" class="text-red-500 hover:text-red-700" @click="removeAgent(agent)">
              <XIcon class="h-5 w-5" />
            </button>
          </div>

          <!-- Badge public si applicable -->
          <div v-if="isPublicAgent(agent)" class="absolute top-2 left-2">
            <span
              class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
              <GlobeIcon class="h-3 w-3 mr-1" />
              Public
            </span>
          </div>

          <!-- Contenu principal -->
          <div class="mb-4" :class="{ 'mt-6': isPublicAgent(agent) }">
            <div class="flex items-center gap-2 mb-2">
              <BuildingIcon v-if="agent.is_legal_entity" class="h-5 w-5 text-gray-600" />
              <UserIcon v-else class="h-5 w-5 text-gray-600" />
              <h3 class="text-lg font-medium">{{ getDisplayName(agent) }}</h3>
            </div>

            <!-- Affiliation pour personne physique -->
            <p v-if="agent.affiliation && !agent.is_legal_entity" class="text-gray-600 text-sm">
              {{ agent.affiliation }}
            </p>

            <!-- Groupe si présent -->
            <p v-if="agent.group" class="text-gray-500 text-xs mt-1">
              Groupe: {{ agent.group }}
            </p>

            <!-- Type d'agent -->
            <div class="mt-2">
              <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                :class="agent.is_legal_entity ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'">
                {{ agent.is_legal_entity ? 'Personne morale' : 'Personne physique' }}
              </span>
            </div>
          </div>

          <!-- Rôles dans ce mémo -->
          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              Rôles dans ce mémo
            </label>
            <TagsPicker v-model="agent.rolesList" placeholder="Ajouter des rôles pour ce mémo..."
              :readonly="readOnlyConfig?.isReadOnly" :disabled="readOnlyConfig?.isReadOnly"
              @update:model-value="() => updateAgent(agent)" />
          </div>

          <!-- Message informatif pour les agents publics -->
          <div v-if="isPublicAgent(agent)" class="mt-2 text-xs text-emerald-700 bg-emerald-100 p-2 rounded">
            <InfoIcon class="h-3 w-3 inline mr-1" />
            Agent public - Les rôles définis ici sont spécifiques à ce mémo
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useAgents } from '@/composables/useAssets';
import type { ReadOnlyConfig } from '@/types/readOnly';
import {
  BookCheckIcon,
  BuildingIcon,
  EditIcon,
  GlobeIcon,
  InfoIcon,
  PlusIcon,
  UserIcon,
  UsersIcon,
  XIcon
} from 'lucide-vue-next';
import type { ComputedRef, Ref } from 'vue';
import { computed, defineComponent, ref, watch } from 'vue';
import type { AgentWithMemos } from '../../api/types/entities/agent/relations';
import type { Agent } from '../../api/types/entities/agent/types';
import type { AgentWithRoles } from '../../types/memoFormData';
import TagsPicker from '../common/TagsPicker.vue';
import AgentCreator from '../creators/AgentCreator.vue';

interface WhoStepData {
  selectedAgents: AgentWithRoles[];
}

export default defineComponent({
  name: 'WhoStep',
  components: {
    TagsPicker,
    AgentCreator,
    XIcon,
    BookCheckIcon,
    UserIcon,
    GlobeIcon,
    PlusIcon,
    UsersIcon,
    InfoIcon,
    BuildingIcon,
    EditIcon
  },
  props: {
    modelValue: {
      type: Object as () => WhoStepData,
      required: true
    },
    userId: {
      type: String,
      required: true
    },
    // Props du système de cache (optionnels, le composable fournit les defaults)
    assetsCacheStore: {
      type: Object,
      default: null
    },
    canEditAsset: {
      type: Function,
      default: null
    },
    isPublicAsset: {
      type: Function,
      default: null
    },
    isMyAsset: {
      type: Function,
      default: null
    },
    readOnlyConfig: {
      type: Object as () => ReadOnlyConfig,
      required: false,
      default: () => ({ isReadOnly: false, allowNavigation: true, showEditHints: true })
    }
  },
  emits: ['update:model-value', 'isValid'],
  setup(props, { emit }) {
    // Utilisation du composable pour le cache avec filtre 'accessible' pour avoir tous les agents
    const {
      allAssets: allAgents,
      isLoading,
      addAsset,
      updateAsset,
      isMyAsset: composableIsMyAsset,
      isPublicAsset: composableIsPublicAsset,
      canEditAsset: composableCanEditAsset
    } = useAgents({ defaultFilter: 'accessible' });

    const selectedAgents: Ref<AgentWithRoles[]> = ref(props.modelValue.selectedAgents || []);
    const searchQuery: Ref<string> = ref('');
    const showCreator: Ref<boolean> = ref(false);
    const showResults: Ref<boolean> = ref(false);
    const creatorMode: Ref<'create' | 'edit'> = ref('create');
    const selectedAgentForEdit: Ref<Agent | undefined> = ref(undefined);

    // Computed property pour vérifier si l'étape est valide
    const isStepValid: ComputedRef<boolean> = computed((): boolean => {
      return selectedAgents.value.length > 0;
    });

    // Watch pour émettre l'état de validité quand il change
    watch(isStepValid, (newValue: boolean): void => {
      emit('isValid', newValue);
    }, { immediate: true });

    // Computed pour séparer mes agents des publics avec recherche
    const myAgents = computed((): AgentWithMemos[] => {
      const filtered = allAgents.value.filter(agent => {
        // Utiliser les helpers du composable ou ceux passés en props
        const isMyAssetFn = props.isMyAsset || composableIsMyAsset;
        return isMyAssetFn(agent);
      });

      // Exclure les agents déjà sélectionnés
      const availableAgents = filtered.filter((agent: Agent): boolean => {
        return !selectedAgents.value.some((selected: AgentWithRoles) => selected.uuid === agent.uuid);
      });

      if (!searchQuery.value) return availableAgents;

      return availableAgents.filter(agent => {
        const searchableFields = ['first_name', 'last_name', 'affiliation', 'roles', 'group'];
        return searchableFields.some(field => {
          const value = agent[field as keyof Agent];
          return typeof value === 'string' && value.toLowerCase().includes(searchQuery.value.toLowerCase());
        }) || getDisplayName(agent).toLowerCase().includes(searchQuery.value.toLowerCase());
      });
    });

    const publicAgents = computed((): AgentWithMemos[] => {
      const filtered = allAgents.value.filter(agent => {
        // Utiliser les helpers du composable ou ceux passés en props
        const isPublicAssetFn = props.isPublicAsset || composableIsPublicAsset;
        return isPublicAssetFn(agent);
      });

      // Exclure les agents déjà sélectionnés
      const availableAgents = filtered.filter((agent: Agent): boolean => {
        return !selectedAgents.value.some((selected: AgentWithRoles) => selected.uuid === agent.uuid);
      });

      if (!searchQuery.value) return availableAgents;

      return availableAgents.filter(agent => {
        const searchableFields = ['first_name', 'last_name', 'affiliation', 'roles', 'group'];
        return searchableFields.some(field => {
          const value = agent[field as keyof Agent];
          return typeof value === 'string' && value.toLowerCase().includes(searchQuery.value.toLowerCase());
        }) || getDisplayName(agent).toLowerCase().includes(searchQuery.value.toLowerCase());
      });
    });

    // Helper pour vérifier si un agent est public
    const isPublicAgent = (agent: Agent | AgentWithRoles): boolean => {
      const isPublicAssetFn = props.isPublicAsset || composableIsPublicAsset;
      return isPublicAssetFn(agent);
    };

    // Helper pour vérifier si on peut éditer un agent
    const canEditAgent = (agent: Agent | AgentWithRoles): boolean => {
      const canEditAssetFn = props.canEditAsset || composableCanEditAsset;
      return canEditAssetFn(agent);
    };

    const handleFocus = (): void => {
      showResults.value = true;
    };

    const handleSearch = (): void => {
      showResults.value = true;
    };

    const resetSearch = (): void => {
      searchQuery.value = '';
      showResults.value = false;
    };

    // Fonction utilitaire pour afficher le nom selon le type d'agent
    const getDisplayName = (agent: Agent | AgentWithRoles): string => {
      if (!agent) return 'Sans nom';
      if (agent.is_legal_entity) {
        return agent.affiliation || 'Personne morale';
      }
      const nameParts: string[] = [];
      if (agent.first_name) nameParts.push(agent.first_name);
      if (agent.last_name) nameParts.push(agent.last_name);
      return nameParts.join(' ') || 'Sans nom';
    };

    // Ouvrir le créateur en mode création
    const openCreator = (): void => {
      if (props.readOnlyConfig?.isReadOnly) return;

      creatorMode.value = 'create';
      selectedAgentForEdit.value = undefined;
      showCreator.value = true;
      resetSearch();
    };

    // Ouvrir le créateur en mode édition
    const editAgent = (agent: AgentWithRoles): void => {
      if (props.readOnlyConfig?.isReadOnly) return;
      if (!canEditAgent(agent)) return;

      creatorMode.value = 'edit';
      selectedAgentForEdit.value = agent as Agent;
      showCreator.value = true;
    };

    // Sélectionner un agent
    const selectAgent = (agent: AgentWithMemos): void => {
      if (props.readOnlyConfig?.isReadOnly) return;

      const agentWithRoles: AgentWithRoles = {
        ...agent,
        rolesList: agent.roles || ''
      };
      selectedAgents.value.push(agentWithRoles);
      updateModelValue();
      resetSearch();
    };

    // Supprimer un agent
    const removeAgent = (agent: AgentWithRoles): void => {
      if (props.readOnlyConfig?.isReadOnly) return;

      selectedAgents.value = selectedAgents.value.filter((a: AgentWithRoles): boolean => a.uuid !== agent.uuid);
      updateModelValue();
    };

    // Mettre à jour l'agent (les rôles sont spécifiques au mémo, donc modifiables pour tous)
    const updateAgent = (agent: AgentWithRoles): void => {
      if (props.readOnlyConfig?.isReadOnly) return;

      const index = selectedAgents.value.findIndex((a: AgentWithRoles): boolean => a.uuid === agent.uuid);
      if (index !== -1) {
        selectedAgents.value[index] = {
          ...agent,
          rolesList: agent.rolesList || ''
        };
        updateModelValue();
      }
    };

    // Mettre à jour le modèle
    const updateModelValue = (): void => {
      emit('update:model-value', {
        ...props.modelValue,
        selectedAgents: selectedAgents.value.map((agent: AgentWithRoles): AgentWithRoles => ({
          ...agent,
          rolesList: agent.rolesList || ''
        }))
      });
      // Émettre l'état de validité après chaque mise à jour
      emit('isValid', isStepValid.value);
    };

    // Gérer la création d'un nouvel agent
    const handleAgentCreated = async (newAgent: Agent): Promise<void> => {
      // Convertir en AgentWithMemos avec un tableau de mémos vide
      const agentWithMemos = { ...newAgent, memos: [] };
      // Ajouter au cache
      addAsset(agentWithMemos);
      selectAgent(agentWithMemos);
      showCreator.value = false;
    };

    // Gérer la mise à jour d'un agent
    const handleAgentUpdated = async (updatedAgent: Agent): Promise<void> => {
      // Trouver l'agent existant pour préserver ses mémos
      const existingAgent = allAgents.value.find(a => a.uuid === updatedAgent.uuid);
      const agentWithMemos = { ...updatedAgent, memos: existingAgent?.memos || [] };
      // Mettre à jour dans le cache
      updateAsset(agentWithMemos);

      // Mettre à jour dans la liste des agents sélectionnés si présent
      const index = selectedAgents.value.findIndex((a: AgentWithRoles): boolean => a.uuid === updatedAgent.uuid);
      if (index !== -1) {
        selectedAgents.value[index] = {
          ...selectedAgents.value[index],
          ...updatedAgent,
          rolesList: selectedAgents.value[index].rolesList // Conserver les rôles du mémo
        };
        updateModelValue();
      }

      showCreator.value = false;
    };

    const handleBlur = (): void => {
      // On utilise setTimeout pour permettre aux clics sur la liste d'être traités avant
      setTimeout((): void => {
        showResults.value = false;
      }, 200);
    };

    return {
      selectedAgents,
      searchQuery,
      showCreator,
      showResults,
      creatorMode,
      selectedAgentForEdit,
      myAgents,
      publicAgents,
      isLoading,
      selectAgent,
      removeAgent,
      updateAgent,
      openCreator,
      editAgent,
      handleAgentCreated,
      handleAgentUpdated,
      handleFocus,
      handleSearch,
      resetSearch,
      handleBlur,
      isStepValid,
      getDisplayName,
      isPublicAgent,
      canEditAgent
    };
  }
});
</script>

<style scoped>
.who-step {
  min-height: 200px;
}

.border-l-3 {
  border-left-width: 3px;
}

.section-header {
  position: sticky;
  top: 0;
  z-index: 10;
}

.anamnesis-card {
  padding: 1rem;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.group:hover .group-hover\:opacity-100 {
  opacity: 1;
}
</style>